import React from 'react';
import Page from './Page';
import Header from './Header';
import EditableField from './EditableField';
import { InformeData } from '../types';

interface InformeViewProps {
  data: InformeData;
  onFieldChange: (key: string, value: string) => void;
  onPatientChange: (field: 'nombre' | 'fecha', value: string) => void;
}


const InformeView: React.FC<InformeViewProps> = ({ data, onFieldChange, onPatientChange }) => {
  const getFieldValue = (key: string) => data.campos[key] || '';

  return (
    <div className="doc">
      {/* ========= PÁGINA 1 ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" />
            <div className="header-title">
              {/* Nombre del paciente */}
              <div className="patient-row">
                <EditableField
                  value={data.paciente.nombre}
                  onChange={(value) => onPatientChange('nombre', value)}
                  fieldKey="nombre_paciente"
                  placeholder="Nombre del paciente"
                  className="inline"
                />
              </div>
              
              {/* Intro */}
              <p><strong>CIP</strong> es una entidad especializada en ofrecer servicios integrales de cuidado de personas, destacándose por su enfoque personalizado.</p>
            </div>
          </div>
        </header>

        <main className="content">
          <br />
          
          <p>A través de su unidad <em>Cuidado Integral de Personas</em>, se centra en proporcionar <strong>calidad de vida</strong> tanto para personas que requieren cuidados especiales con enfermedades ya de base (cáncer, enf. inmunológicas, metabólicas) como para aquellos que buscan mejorar su estilo de vida y longevidad.</p>

          {/* Mensaje central en rojo */}
          <p style={{textAlign: 'center', fontWeight: 'bold', color: '#c00'}}>
            Lo que ofrecemos es un cambio de estilo de vida.
          </p>

          {/* Explicación estilo de vida */}
          <p>La medicina y el estilo de vida están profundamente interconectados, ya que las decisiones cotidianas que tomamos sobre nuestra salud y bienestar emocional pueden tener un impacto significativo en nuestra calidad de vida y en la prevención de enfermedades y longevidad.</p>

          <p>Un estilo de vida saludable implica tomar decisiones conscientes sobre <strong>alimentación</strong>, <strong>ejercicio</strong>, <strong>descanso</strong>, <strong>manejo del estrés</strong> y hábitos sociales "tribu".</p>

          {/* Epigenética */}
          <h3>¿Por qué es tan importante el estilo de vida? ¿Cómo nos influye?</h3>

          {/* Imagen epigenética a la derecha */}
          <figure className="figure-right avoid-break">
            <img src="/img/imagen_1.jpeg" alt="Epigenética: ADN y Ambiente" />
          </figure>

          <p>La respuesta es la <strong>epigenética</strong>, un campo fascinante de la biología que estudia los cambios en la expresión genética que no implican alteraciones en la secuencia del ADN, pero que pueden influir en cómo se activan o desactivan los genes.</p>

          <p>Estos cambios epigenéticos son clave para entender cómo factores ambientales —como la alimentación, el estrés, el ejercicio y otros aspectos del estilo de vida— pueden afectar la salud a lo largo de toda la vida y, en algunos casos, incluso ser transmitidos a las siguientes generaciones.</p>

          <p>En rasgos generales podemos hablar de un <strong>20% genética</strong> y un <strong>80% epigenética</strong>: la genética establece la predisposición y la epigenética determina el grado en que esa predisposición se manifiesta.</p>

          <p>Los factores ambientales, incluidos la dieta, la actividad física, la exposición a toxinas (como el humo del tabaco), el estrés y la falta de sueño, pueden modificar la forma en que se expresan nuestros genes, incluso si no alteran la secuencia genética subyacente. Esto significa que nuestro estilo de vida puede influir en nuestra biología a un nivel muy profundo.</p>
        </main>
      </Page>

      {/* ========= PÁGINA 2 ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" />
            <div className="header-title">
              <p>
                Muchas formas de cáncer están relacionadas con cambios epigenéticos. Por ejemplo,
                ciertos genes supresores de tumores pueden ser "apagados" por modificaciones epigenéticas,
                mientras que otros genes que promueven el crecimiento tumoral pueden ser "activados".
              </p>
            </div>
          </div>
        </header>

        <main className="content">
          <div className="two-col">
            <div className="col">
              <p>
                Enfermedades como la depresión, la esquizofrenia y el trastorno bipolar se han
                relacionado con alteraciones epigenéticas, especialmente en cómo los genes responden a
                factores estresantes y a los cambios en el ambiente. Las modificaciones epigenéticas pueden influir en el metabolismo y en la inflamación,
                contribuyendo al desarrollo de condiciones como la diabetes tipo 2, la hipertensión y la
                aterosclerosis; así como no olvidar el sistema inmunológico, que responde a los propios
                tejidos del cuerpo, lo que puede contribuir al desarrollo de enfermedades autoinmunes.
              </p>

              {/* Imagen central */}
              <figure className="figure-wide avoid-break" style={{maxWidth: '160mm', margin: '6mm auto'}}>
                <img src="/img/imagen_2.jpeg" alt="Hábitos de vida y efectos epigenéticos" />
              </figure>

              <p style={{textAlign: 'center', fontWeight: '700', color: '#c00', margin: '6mm 0'}}>
                La epigenética subraya la increíble capacidad que tenemos para
                "modificar nuestra biología a través de nuestras elecciones diarias".
              </p>

              <p>
                Un enfoque multidisciplinar permite abordar enfermedades complejas que requieren la
                intervención de diferentes especialistas para obtener una solución completa, aunando la
                medicina convencional con estrategias complementarias.
              </p>

              <p>
                CIP está formado por un conjunto de especialistas sanitarios: médicos especialistas en
                oncología médica, medicina interna y medicina de familia, fisioterapeutas, psicología y
                terapia, preparadores físicos, PNI y podólogos.
              </p>

              <p>
                Buscamos tratar al paciente de manera integral, considerando no solo los síntomas físicos,
                sino también sus necesidades emocionales, sociales y psicológicas.
              </p>

              <p>
                Cuando un paciente ingiere varios medicamentos, nuestro personal médico analiza con un
                software especializado las posibles interacciones entre dichos medicamentos u otras
                sustancias de consumo habitual, como pueden ser infusiones, especias, alimentos, etc. El
                objetivo de este estudio es prevenir efectos secundarios indeseados y optimizar la función
                de los medicamentos.
              </p>
            </div>
          </div>
        </main>
      </Page>

      {/* ========= PÁGINA 3 ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" />
            <div className="header-title">
              <p style={{fontWeight: '700'}}>
                Tus hábitos de vida te han llevado a estar en la situación actual, por lo que un cambio de estos hábitos será la ruta a la mejoría.
              </p>
            </div>
          </div>
        </header>

        <main className="content">
          <br />

          <p>
            Este cambio de estilo de vida puede conllevar también un cambio de hábito alimenticio. Una dieta implica un día de inicio y un día de fin.
            ¿Quieres estar saludable durante unos meses, o prefieres estar saludable toda la vida?
          </p>

          {/* Cita/aviso en una caja suave */}
          <div style={{background: '#f7eaea', borderLeft: '3px solid #c00', padding: '4mm 5mm', margin: '4mm 0', textAlign: 'center', fontWeight: '700', color: '#333'}}>
            No podemos pretender que las cosas cambien si seguimos haciendo lo mismo.<br />
            <span style={{fontWeight: '400', fontStyle: 'italic'}}>(Albert Einstein, físico)</span>
          </div>

          <p>
            Por estos motivos CIP te recomendamos encarecidamente que tengas una alimentación extremadamente variada y no pases por alto nuestras recomendaciones.
          </p>

          <p>
            Es por eso que los alimentos catalogados como <strong>no recomendados</strong> son alimentos que deberías evitar no sólo mientras te encuentres en fase de recuperación, sino como medida preventiva. De la misma manera los alimentos que recomendamos consumir frecuentemente deberías de incorporarlos a tu alimentación incluso una vez superada la fase de recuperación, ya que igual de perjudicial es alimentarse con algo potencialmente tóxico o dañino, que dejar de recibir nutrientes o micronutrientes. Las dos situaciones generan enfermedad. La clave de la salud es, entre otros motivos, consumir cuanta más diversidad de alimentos saludables mejor.
          </p>

          <p style={{textTransform: 'uppercase', color: '#c00', fontWeight: '700', textDecoration: 'underline', marginTop: '6mm'}}>
            Estrategias de tratamientos basadas en evidencia científica:
          </p>

          <p>
            Las estrategias que te recomendamos para tu tratamiento te pueden parecer exageradas y que van en contra de nuestro estado de confort, pero su utilidad terapéutica está más que demostrada científicamente. Su enfoque no es restablecer un ritmo fisiológico, generar un cambio metabólico e inmunológico y estimular a un área del cerebro que se denomina hipotálamo, que se encarga del grado de mantener estables casi todos los parámetros importantes para mantener una salud óptima.
          </p>

          <p>
            Esto lo conseguimos con diferentes estrategias: alimentación, medicación, actividad física, hábitos de vida, ejercicios respiratorios, ejercicios de temperatura… Posiblemente te recomendemos cambios que te resultarán "raros" para todavía la mayoría de la sociedad de hoy en día, pero todos ellos estarán justificados científicamente. <strong>CONFÍA.</strong>
          </p>

          <p style={{textAlign: 'center', marginTop: '6mm'}}>
            Te la detallamos a continuación.
          </p>
        </main>
      </Page>

      {/* ========= PÁGINA 4 ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" style={{marginTop: '-8mm'}} />
            <div className="header-title">
              <h3 style={{color: '#c00', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4mm'}}>
                Farmacología y suplementación:
              </h3>
              <p>
                Los suplementos que te indicamos a continuación son productos avalados científicamente,
                no interaccionan con ningún tipo de fármaco que estés tomando. Igualmente no generan
                adicción, pretenden restablecer algunos nutrientes, ayudar a mitigar los efectos secundarios de
                los tratamientos farmacológicos sin restarle eficacia, aumentar la modulación de nuestro sistema inmune o simplemente intentar o ayudar a frenar directamente tu enfermedad.
              </p>
            </div>
          </div>
        </header>

        <main className="content">
          <br /><br /><br /><br />
          
          <p>
            Los puedes encontrar o encargar en cualquier parafarmacia y herbolario especializado. Los fármacos indicados en el tratamiento están indicados para tu enfermedad y no interaccionan
            con otros que ya estés usando.
          </p>

          <EditableField
            value={getFieldValue('farmacologia_y_suplementacion')}
            onChange={(value) => onFieldChange('farmacologia_y_suplementacion', value)}
            fieldKey="farmacologia_y_suplementacion"
            multiline
            placeholder="Farmacología y suplementación..."
            className="large-textarea"
          />
          <br />
        </main>
      </Page>

      {/* ========= PÁGINA 5 ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" />
            <div className="header-title">
              <h3 style={{color: '#c00', margin: '0 0 6mm 0'}}>ESTRATEGIAS PARA EL ESTILO DE VIDA:</h3>
            </div>
          </div>
        </header>

        <main className="content">
          <br />
          
          <ul style={{listStyle: 'disc', paddingLeft: '6mm', margin: '4mm 0 0 0'}}>
            {/* 1. Rutina y ciclo circadiano */}
            <li className="avoid-header avoid-break" style={{marginBottom: '4mm'}}>
              <div style={{fontWeight: '700', margin: '0 0 2mm 0'}}>RUTINA Y CICLO CIRCADIANO:</div>
            </li>
            <EditableField
              value={getFieldValue('p6_rutina_circadiano')}
              onChange={(value) => onFieldChange('p6_rutina_circadiano', value)}
              fieldKey="p6_rutina_circadiano"
              placeholder="RUTINA Y CICLO CIRCADIANO..."
              multiline
              className="medium-textarea"
            />

            {/* 2. Actividad y deporte */}
            <li className="avoid-break" style={{marginBottom: '4mm'}}>
              <div style={{fontWeight: '700', margin: '0 0 2mm 0'}}>ACTIVIDAD Y DEPORTE:</div>
            </li>
            <EditableField
              value={getFieldValue('p6_actividad_deporte')}
              onChange={(value) => onFieldChange('p6_actividad_deporte', value)}
              fieldKey="p6_actividad_deporte"
              placeholder="ACTIVIDAD Y DEPORTE..."
              multiline
              className="medium-textarea"
            />

            {/* 3. Estrés */}
            <li className="avoid-break" style={{marginBottom: '4mm'}}>
              <div style={{fontWeight: '700', margin: '0 0 2mm 0'}}>ESTRÉS:</div>
            </li>
            <EditableField
              value={getFieldValue('p6_estres')}
              onChange={(value) => onFieldChange('p6_estres', value)}
              fieldKey="p6_estres"
              placeholder="ESTRÉS..."
              multiline
              className="medium-textarea"
            />

            {/* 4. Modo de vida */}
            <li className="avoid-break" style={{marginBottom: '0'}}>
              <div style={{fontWeight: '700', margin: '0 0 2mm 0'}}>
                MODO DE VIDA (AYUNO, FRÍO, MEDITACIÓN, HIPOXIA, HIPERCAPNIA, SAUNA, HIGIENE BUCODENTAL, HIDRATACIÓN, ELIMINACIÓN DE TÓXICOS…):
              </div>
            </li>
            <EditableField
              value={getFieldValue('p6_modo_de_vida')}
              onChange={(value) => onFieldChange('p6_modo_de_vida', value)}
              fieldKey="p6_modo_de_vida"
              placeholder="MODO DE VIDA..."
              multiline
              className="medium-textarea"
            />
          </ul>
        </main>
      </Page>

      {/* ========= PÁGINA 6 ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" />
            <div className="header-title">
              <h3 style={{color: '#c00', margin: '0 0 6mm 0'}}>RECOMENDACIONES ALIMENTARIAS:</h3>
              <p className="avoid-header" style={{marginBottom: '4mm'}}>
                Antes de detallar tus especificaciones alimenticias, es importante que sepas algo muy importante sobre dos grandes grupos.
              </p>
            </div>
          </div>
        </header>

        <main className="content">
          <br />

          <p style={{marginBottom: '4mm'}}>
            El primero son los <strong>alimentos ultraprocesados</strong>, están asociados con un alto riesgo de desarrollar enfermedades crónicas. A pesar de su alta densidad calórica, los ultraprocesados suelen carecer de nutrientes esenciales como vitaminas, minerales y fibra, lo que puede contribuir a deficiencias nutricionales.
          </p>

          <p style={{marginBottom: '8mm'}}>
            El segundo son los <strong>azúcares</strong>. Existen dos tipos: los que vienen naturalmente en alimentos reales (frutas, tubérculos…) y los <strong>azúcares añadidos</strong>. Estos azúcares añadidos son realmente perjudiciales para la salud e innecesarios para nuestra supervivencia. Evitarlos en la alimentación mejorará muchos factores de tu salud y prevendrá muchas enfermedades.
          </p>

          {/* Tabla de recomendaciones */}
          <table className="food-matrix avoid-header" style={{width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', margin: '0 0 8mm 0'}}>
            <colgroup>
              <col style={{width: '28%'}} />
              <col style={{width: '24%'}} />
              <col style={{width: '24%'}} />
              <col style={{width: '24%'}} />
            </colgroup>

            <thead>
              <tr>
                <th style={{border: '0.3mm solid #333', textAlign: 'left', padding: '2mm', background: '#fff'}}>&nbsp;</th>
                <th style={{border: '0.3mm solid #333', textAlign: 'center', padding: '2mm', background: '#cfe8cf'}}><strong>RECOMENDADO</strong></th>
                <th style={{border: '0.3mm solid #333', textAlign: 'center', padding: '2mm', background: '#e8efdd'}}><strong>OCASIONALMENTE</strong></th>
                <th style={{border: '0.3mm solid #333', textAlign: 'center', padding: '2mm', background: '#f4c6cc'}}><strong>NO RECOMENDADO</strong></th>
              </tr>
            </thead>

            <tbody>
              {/* PESCADOS */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>PESCADOS</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField
                    value={getFieldValue('p6_pescados_rec')}
                    onChange={(value) => onFieldChange('p6_pescados_rec', value)}
                    fieldKey="p6_pescados_rec"
                    multiline
                    className="table-field"
                  />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField
                    value={getFieldValue('p6_pescados_ocas')}
                    onChange={(value) => onFieldChange('p6_pescados_ocas', value)}
                    fieldKey="p6_pescados_ocas"
                    multiline
                    className="table-field"
                  />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField
                    value={getFieldValue('p6_pescados_no')}
                    onChange={(value) => onFieldChange('p6_pescados_no', value)}
                    fieldKey="p6_pescados_no"
                    multiline
                    className="table-field"
                  />
                </td>
              </tr>

              {/* CARNES */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>CARNES</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_carnes_rec')} onChange={(value) => onFieldChange('p6_carnes_rec', value)} fieldKey="p6_carnes_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_carnes_ocas')} onChange={(value) => onFieldChange('p6_carnes_ocas', value)} fieldKey="p6_carnes_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_carnes_no')} onChange={(value) => onFieldChange('p6_carnes_no', value)} fieldKey="p6_carnes_no" multiline className="table-field" />
                </td>
              </tr>

              {/* VERDURAS Y HORTALIZAS */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>VERDURAS Y<br />HORTALIZAS</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_verduras_rec')} onChange={(value) => onFieldChange('p6_verduras_rec', value)} fieldKey="p6_verduras_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_verduras_ocas')} onChange={(value) => onFieldChange('p6_verduras_ocas', value)} fieldKey="p6_verduras_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_verduras_no')} onChange={(value) => onFieldChange('p6_verduras_no', value)} fieldKey="p6_verduras_no" multiline className="table-field" />
                </td>
              </tr>

              {/* FRUTAS */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>FRUTAS</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_frutas_rec')} onChange={(value) => onFieldChange('p6_frutas_rec', value)} fieldKey="p6_frutas_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_frutas_ocas')} onChange={(value) => onFieldChange('p6_frutas_ocas', value)} fieldKey="p6_frutas_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_frutas_no')} onChange={(value) => onFieldChange('p6_frutas_no', value)} fieldKey="p6_frutas_no" multiline className="table-field" />
                </td>
              </tr>

              {/* SETAS */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>SETAS</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_setas_rec')} onChange={(value) => onFieldChange('p6_setas_rec', value)} fieldKey="p6_setas_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_setas_ocas')} onChange={(value) => onFieldChange('p6_setas_ocas', value)} fieldKey="p6_setas_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_setas_no')} onChange={(value) => onFieldChange('p6_setas_no', value)} fieldKey="p6_setas_no" multiline className="table-field" />
                </td>
              </tr>

              {/* LEGUMBRES */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>LEGUMBRES</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_legumbres_rec')} onChange={(value) => onFieldChange('p6_legumbres_rec', value)} fieldKey="p6_legumbres_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_legumbres_ocas')} onChange={(value) => onFieldChange('p6_legumbres_ocas', value)} fieldKey="p6_legumbres_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_legumbres_no')} onChange={(value) => onFieldChange('p6_legumbres_no', value)} fieldKey="p6_legumbres_no" multiline className="table-field" />
                </td>
              </tr>

              {/* CEREALES */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>CEREALES (arroz, harinas, pasta, avena)</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_cereales_rec')} onChange={(value) => onFieldChange('p6_cereales_rec', value)} fieldKey="p6_cereales_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_cereales_ocas')} onChange={(value) => onFieldChange('p6_cereales_ocas', value)} fieldKey="p6_cereales_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_cereales_no')} onChange={(value) => onFieldChange('p6_cereales_no', value)} fieldKey="p6_cereales_no" multiline className="table-field" />
                </td>
              </tr>

              {/* TUBÉRCULOS */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>TUBÉRCULOS</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_tuberculos_rec')} onChange={(value) => onFieldChange('p6_tuberculos_rec', value)} fieldKey="p6_tuberculos_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_tuberculos_ocas')} onChange={(value) => onFieldChange('p6_tuberculos_ocas', value)} fieldKey="p6_tuberculos_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_tuberculos_no')} onChange={(value) => onFieldChange('p6_tuberculos_no', value)} fieldKey="p6_tuberculos_no" multiline className="table-field" />
                </td>
              </tr>

              {/* BEBIDAS VEGETALES */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>BEBIDAS VEGETALES</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_bebidas_rec')} onChange={(value) => onFieldChange('p6_bebidas_rec', value)} fieldKey="p6_bebidas_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_bebidas_ocas')} onChange={(value) => onFieldChange('p6_bebidas_ocas', value)} fieldKey="p6_bebidas_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_bebidas_no')} onChange={(value) => onFieldChange('p6_bebidas_no', value)} fieldKey="p6_bebidas_no" multiline className="table-field" />
                </td>
              </tr>

              {/* PRODUCTOS LÁCTEOS */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>PRODUCTOS LÁCTEOS</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_lacteos_rec')} onChange={(value) => onFieldChange('p6_lacteos_rec', value)} fieldKey="p6_lacteos_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_lacteos_ocas')} onChange={(value) => onFieldChange('p6_lacteos_ocas', value)} fieldKey="p6_lacteos_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_lacteos_no')} onChange={(value) => onFieldChange('p6_lacteos_no', value)} fieldKey="p6_lacteos_no" multiline className="table-field" />
                </td>
              </tr>

              {/* MARISCOS */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>MARISCOS</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_mariscos_rec')} onChange={(value) => onFieldChange('p6_mariscos_rec', value)} fieldKey="p6_mariscos_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_mariscos_ocas')} onChange={(value) => onFieldChange('p6_mariscos_ocas', value)} fieldKey="p6_mariscos_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_mariscos_no')} onChange={(value) => onFieldChange('p6_mariscos_no', value)} fieldKey="p6_mariscos_no" multiline className="table-field" />
                </td>
              </tr>

              {/* EMBUTIDOS */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>EMBUTIDOS</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_embutidos_rec')} onChange={(value) => onFieldChange('p6_embutidos_rec', value)} fieldKey="p6_embutidos_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_embutidos_ocas')} onChange={(value) => onFieldChange('p6_embutidos_ocas', value)} fieldKey="p6_embutidos_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_embutidos_no')} onChange={(value) => onFieldChange('p6_embutidos_no', value)} fieldKey="p6_embutidos_no" multiline className="table-field" />
                </td>
              </tr>

              {/* HUEVOS */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>HUEVOS</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_huevos_rec')} onChange={(value) => onFieldChange('p6_huevos_rec', value)} fieldKey="p6_huevos_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_huevos_ocas')} onChange={(value) => onFieldChange('p6_huevos_ocas', value)} fieldKey="p6_huevos_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_huevos_no')} onChange={(value) => onFieldChange('p6_huevos_no', value)} fieldKey="p6_huevos_no" multiline className="table-field" />
                </td>
              </tr>

              {/* DULCES Y CHOCOLATES */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>DULCES Y<br />CHOCOLATES</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_dulces_rec')} onChange={(value) => onFieldChange('p6_dulces_rec', value)} fieldKey="p6_dulces_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_dulces_ocas')} onChange={(value) => onFieldChange('p6_dulces_ocas', value)} fieldKey="p6_dulces_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_dulces_no')} onChange={(value) => onFieldChange('p6_dulces_no', value)} fieldKey="p6_dulces_no" multiline className="table-field" />
                </td>
              </tr>

              {/* FRUTOS SECOS Y SEMILLAS */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>FRUTOS SECOS Y<br />SEMILLAS</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_frutos_rec')} onChange={(value) => onFieldChange('p6_frutos_rec', value)} fieldKey="p6_frutos_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_frutos_ocas')} onChange={(value) => onFieldChange('p6_frutos_ocas', value)} fieldKey="p6_frutos_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_frutos_no')} onChange={(value) => onFieldChange('p6_frutos_no', value)} fieldKey="p6_frutos_no" multiline className="table-field" />
                </td>
              </tr>

              {/* AGUA */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>AGUA</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_agua_rec')} onChange={(value) => onFieldChange('p6_agua_rec', value)} fieldKey="p6_agua_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_agua_ocas')} onChange={(value) => onFieldChange('p6_agua_ocas', value)} fieldKey="p6_agua_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_agua_no')} onChange={(value) => onFieldChange('p6_agua_no', value)} fieldKey="p6_agua_no" multiline className="table-field" />
                </td>
              </tr>

              {/* CAFÉ, INFUSIONES */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>CAFÉ, INFUSIONES</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_cafe_rec')} onChange={(value) => onFieldChange('p6_cafe_rec', value)} fieldKey="p6_cafe_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_cafe_ocas')} onChange={(value) => onFieldChange('p6_cafe_ocas', value)} fieldKey="p6_cafe_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_cafe_no')} onChange={(value) => onFieldChange('p6_cafe_no', value)} fieldKey="p6_cafe_no" multiline className="table-field" />
                </td>
              </tr>

              {/* ENCURTIDOS Y SALSAS */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>ENCURTIDOS Y<br />SALSAS</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_encurtidos_rec')} onChange={(value) => onFieldChange('p6_encurtidos_rec', value)} fieldKey="p6_encurtidos_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_encurtidos_ocas')} onChange={(value) => onFieldChange('p6_encurtidos_ocas', value)} fieldKey="p6_encurtidos_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_encurtidos_no')} onChange={(value) => onFieldChange('p6_encurtidos_no', value)} fieldKey="p6_encurtidos_no" multiline className="table-field" />
                </td>
              </tr>

              {/* ALGAS */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>ALGAS</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_algas_rec')} onChange={(value) => onFieldChange('p6_algas_rec', value)} fieldKey="p6_algas_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_algas_ocas')} onChange={(value) => onFieldChange('p6_algas_ocas', value)} fieldKey="p6_algas_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_algas_no')} onChange={(value) => onFieldChange('p6_algas_no', value)} fieldKey="p6_algas_no" multiline className="table-field" />
                </td>
              </tr>

              {/* SAL Y ESPECIAS */}
              <tr>
                <td style={{border: '0.3mm solid #333', padding: '2mm'}}>SAL Y ESPECIAS</td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_sal_rec')} onChange={(value) => onFieldChange('p6_sal_rec', value)} fieldKey="p6_sal_rec" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_sal_ocas')} onChange={(value) => onFieldChange('p6_sal_ocas', value)} fieldKey="p6_sal_ocas" multiline className="table-field" />
                </td>
                <td style={{border: '0.3mm solid #333', padding: '0'}}>
                  <EditableField value={getFieldValue('p6_sal_no')} onChange={(value) => onFieldChange('p6_sal_no', value)} fieldKey="p6_sal_no" multiline className="table-field" />
                </td>
              </tr>
            </tbody>
          </table>

          {/* Bloque "Alimentos picantes" */}
          <div className="avoid-break" style={{fontWeight: '700', margin: '2mm 0 2mm 0'}}>ALIMENTOS PICANTES:</div>
          <EditableField
            value={getFieldValue('p6_alimentos_picantes')}
            onChange={(value) => onFieldChange('p6_alimentos_picantes', value)}
            fieldKey="p6_alimentos_picantes"
            placeholder="ALIMENTOS PICANTES..."
            multiline
            className="block"
          />
        </main>
      </Page>

      {/* ========= PÁGINA 7 ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" />
            <div className="header-title">
              <h3 style={{color: '#c00', margin: '0 0 6mm 0'}}>PLANIFICACIÓN DE PROCEDIMIENTOS:</h3>
            </div>
          </div>
        </header>

        <main className="content">
          <br />

          <EditableField
            value={getFieldValue('p7_planificacion_procedimientos')}
            onChange={(value) => onFieldChange('p7_planificacion_procedimientos', value)}
            fieldKey="p7_planificacion_procedimientos"
            multiline
            className="large-textarea"
            placeholder="Planificación de procedimientos..."
          />

          {/* Aviso importante */}
          <div style={{
            margin: '4mm auto 0',
            padding: '2mm 4mm',
            width: '80%',
            border: '0.35mm solid #e0c5a5',
            background: '#fff6ee',
            borderRadius: '2mm',
            fontSize: '9pt',
            lineHeight: '1.2',
            color: '#c00',
            textAlign: 'center'
          }}>
            <div style={{fontWeight: 'bold', color: '#c00', marginBottom: '1mm', fontSize: '10pt'}}>
              IMPORTANTE
            </div>
            <div style={{fontSize: '9pt', lineHeight: '1.2'}}>
              Es muy frecuente que, cuando se comienza a establecer nuevos hábitos de vida, aparezcan durante unos días algunos síntomas como cefaleas, cansancio o dolores. No te alarmes. Esto no debe constituir un motivo para abandonar nuestras recomendaciones. Estos síntomas no suelen persistir más de 7–10 días.
            </div>
          </div>
        </main>
      </Page>

      {/* ========= PÁGINA 8 - Bibliografía ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" />
            <div className="header-title">
              <h3 style={{margin: '0 0 6mm 0'}}>Bibliografía que justifica nuestros tratamientos</h3>
            </div>
          </div>
        </header>

        <main className="content avoid-header">
          <br />
          
          {/* 1) Dieta y evitación de procesados */}
          <h4 style={{margin: '0 0 3mm 0'}}>1. Dieta y evitación de procesados</h4>
          <ol style={{margin: '0 0 8mm 0', paddingLeft: '6mm'}}>
            <li>
              Dai, S., Liu, D., Wang, H., <em>et al.</em> (2024). Ultra-processed foods and human health: An umbrella review of meta-analyses.
              <em> Clinical Nutrition</em>, 43(3), 602–613.
              <a href="https://doi.org/10.1016/j.clnu.2024.03.027" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.1016/j.clnu.2024.03.027</a>
            </li>
            <li>
              Lane, M. M., Davis, J. A., Beattie, S., <em>et al.</em> (2024). Ultra-processed food exposure and adverse health outcomes: umbrella review of epidemiological meta-analyses.
              <em> BMJ</em>, 384, e077310.
              <a href="https://doi.org/10.1136/bmj-2023-077310" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.1136/bmj-2023-077310</a>
            </li>
            <li>
              Vitale, M., Sparano, C., La Vecchia, C., <em>et al.</em> (2024). Ultra-Processed Foods and Human Health: A Systematic Review and Meta-Analysis of Prospective Cohort Studies.
              <em> Nutrition and Health</em>, 30(1), 56–84.
              <a href="https://doi.org/10.1177/02601060231223313" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.1177/02601060231223313</a>
            </li>
            <li>
              Hall, K. D., Ayuketah, A., Brychta, R., <em>et al.</em> (2019). Ultra-processed diets cause excess calorie intake and weight gain: an inpatient randomized controlled trial of ad libitum food intake.
              <em> Cell Metabolism</em>, 30(1), 67–77.
              <a href="https://doi.org/10.1016/j.cmet.2019.05.008" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.1016/j.cmet.2019.05.008</a>
            </li>
            <li>
              Gomes Gonçalves Ferreira, L., da Costa Louzada, M. L., Steele, E. M., <em>et al.</em> (2020). Consumption of ultra-processed foods and health status: a systematic review and meta-analysis.
              <em> Nutrition Reviews</em>, 78(11), 1059–1074.
              <a href="https://doi.org/10.1093/nutrit/nuaa038" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.1093/nutrit/nuaa038</a>
            </li>
          </ol>

          {/* 2) Ejercicio */}
          <h4 style={{margin: '0 0 3mm 0'}}>2. Ejercicio físico multimodal como prevención y tratamiento</h4>
          <ol style={{margin: '0', paddingLeft: '6mm'}} start={1}>
            <li>
              Feito, Y., Heinrich, K. M., Butcher, S. J., &amp; Poston, W. S. C. (2018). High-Intensity Multimodal Training (HIMT) for fitness promotion.
              <em> Frontiers in Physiology</em>, 12, 723486.
              <a href="https://doi.org/10.3389/fphys.2021.723486" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.3389/fphys.2021.723486</a>
            </li>
            <li>
              Meneses-Echávez, J. F., González-Jiménez, E., &amp; Ramírez-Vélez, R. (2015). Effects of supervised multimodal exercise interventions on cancer-related fatigue: systematic review and meta-analysis of randomized controlled trials.
              <em> Asian Pacific Journal of Cancer Prevention</em>, 16(13), 4713–4719.
              <a href="https://doi.org/10.7314/APJCP.2015.16.13.4713" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.7314/APJCP.2015.16.13.4713</a>
            </li>
            <li>
              Carnes, J., &amp; Mahoney, T. (2019). The effect of multimodal exercise interventions on older adults: a systematic review.
              <em> Journal of Aging and Physical Activity</em>, 27(5), 679–690.
              <a href="https://doi.org/10.1123/japa.2018-0159" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.1123/japa.2018-0159</a>
            </li>
            <li>
              World Health Organization. (2020). <em>Guidelines on physical activity and sedentary behaviour</em>. Geneva: WHO.
              <a href="https://www.who.int/publications/i/item/9789240015128" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://www.who.int/publications/i/item/9789240015128</a>
            </li>
            <li>
              Claudino, J. G., Gabbett, T. J., Bourgeois, F., <em>et al.</em> (2018). CrossFit overview: Systematic review and meta-analysis.
              <em> Sports Medicine – Open</em>, 4(1), 1–14.
              <a href="https://doi.org/10.1186/s40798-018-0124-5" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.1186/s40798-018-0124-5</a>
            </li>
          </ol>
        </main>
      </Page>

      {/* ========= PÁGINA 9 ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" />
            <div className="header-title"></div>
          </div>
        </header>

        <main className="content avoid-header">
          <br />
          
          <ol style={{margin: '0', paddingLeft: '6mm'}}>
            {/* 3) Sueño */}
            <div style={{fontWeight: '700', margin: '0 0 3mm 0'}}>3. Calidad de sueño y optimización no farmacológica</div>
            <ol style={{margin: '0', paddingLeft: '6mm'}}>
              <li>
                Wang, Y., Xu, B., Wang, X., <em>et al.</em> (2024). Efficacy of non-pharmacological interventions for sleep quality in adults: a network meta-analysis.
                <em> Frontiers in Neuroscience</em>, 18.
                <a href="https://doi.org/10.3389/fnins.2024.1337619" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.3389/fnins.2024.1337619</a>
              </li>
              <li>
                Wu, Y., Tang, Q., Wang, X., <em>et al.</em> (2023). The effectiveness of non-pharmacological sleep interventions for improving inpatient sleep quality: a systematic review and meta-analysis.
                <em> Sleep Medicine Reviews</em>, 68, 101801.
                <a href="https://doi.org/10.1016/j.smrv.2023.101801" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.1016/j.smrv.2023.101801</a>
              </li>
              <li>
                Harvey, A. G., &amp; Buysse, D. J. (2018). Non-pharmacological strategies for improving sleep: mindfulness, behavioral therapy and sleep hygiene.
                <em> Sleep Medicine Reviews</em>, 25, 23–36.
                <a href="https://doi.org/10.1016/j.smrv.2015.11.003" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.1016/j.smrv.2015.11.003</a>
                — Morin, C. M., &amp; Benca, R. (2012). Insomnia.
                <em> The Lancet</em>, 379(9821), 1129–1141.
                <a href="https://doi.org/10.1016/S0140-6736(11)60750-2" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.1016/S0140-6736(11)60750-2</a>
              </li>
              <li>
                Chong, Y., &amp; Cheung, J. M. (2017). Cognitive behavioral therapy for insomnia: a meta-analysis of randomized controlled trials.
                <em> Journal of the American Medical Association</em>, 325(1), 21–35.
              </li>
            </ol>
            
            <br />
            
            {/* 4) Suplementación */}
            <div style={{fontWeight: '700', margin: '0 0 3mm 0'}}>4. Suplementación básica para personas de edad media en Europa</div>
            <ol style={{margin: '0', paddingLeft: '6mm'}}>
              <li>
                Zembura, P., Muntasewicz, K., Metelska, P., <em>et al.</em> (2023). Is dietary (food) supplement intake reported in European national nutrition surveys?
                <em> Nutrients</em>, 15(1), 236.
                <a href="https://doi.org/10.3390/nu15010236" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.3390/nu15010236</a>
              </li>
              <li>
                Pop, M. S., Pop, L. C., &amp; Năstase, M. S. (2023). Exploring the potential benefits of natural calcium-rich mineral waters: a systematic review.
                <em> Nutrients</em>, 15(7), 1587.
                <a href="https://doi.org/10.3390/nu15071587" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.3390/nu15071587</a>
              </li>
              <li>
                Kennedy, D. O. (2016). B vitamins and the brain: mechanisms, dose and efficacy—review.
                <em> Nutrients</em>, 8(2), 68.
                <a href="https://doi.org/10.3390/nu8020068" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.3390/nu8020068</a>
                — EFSA Panel on Nutrition, Novel Foods and Food Allergens (NDA) <em>et al.</em> (2016). Scientific opinion on nutrient requirements and dietary recommendations for older adults in the European Union.
                <em> EFSA Journal</em>, 14(2), 4300.
                <a href="https://doi.org/10.2903/j.efsa.2016.4300" target="_blank" rel="noopener" style={{wordBreak: 'break-word'}}>https://doi.org/10.2903/j.efsa.2016.4300</a>
              </li>
              <li>
                Cruz-Jentoft, A. J., Landi, F., Topinková, E., &amp; Michel, J. P. (2008). Understanding sarcopenia as a geriatric syndrome.
                <em> Current Opinion in Clinical Nutrition &amp; Metabolic Care</em>, 13(1), 1–7.
              </li>
            </ol>
          </ol>
        </main>
      </Page>

      {/* ========= PÁGINA 10 ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" />
            <div className="header-title"></div>
          </div>
        </header>

        <main className="content">
          <br />
          
          <h4 style={{margin: '0 0 3mm 0'}}>5. Ingesta de agua y minerales</h4>
          <ul style={{margin: '0 0 6mm 0', paddingLeft: '6mm', listStyle: 'disc'}}>
            <li>
              Pop, M. S., Pop, L. C., & Năstasă, M. S. (2023). Exploring the potential benefits of natural calcium-rich mineral waters: a systematic review. <em>Nutrients</em>, 15(7), 1587.
              <a href="https://doi.org/10.3390/nu15071587" target="_blank" rel="noopener">https://doi.org/10.3390/nu15071587</a>
            </li>
            <li>
              Healthline. (2019). Does mineral water have health benefits? Human studies and evidence summarized.
            </li>
            <li>
              Ferraro, P. M., Curhan, G. C., Gambaro, G., & Taylor, E. N. (2015). Intake of water and beverages and the risk of kidney stones in men and women.
              <em> The American Journal of Clinical Nutrition</em>, 102(6), 1565–1575.
            </li>
            <li>
              Sabatier, M., Hariton, F., Darmon, P., et al. (2017). The bioavailability of calcium from fortified mineral water: A systematic review. <em>European Journal of Clinical Nutrition</em>, 71(3), 275–282.
            </li>
            <li>
              Vannucci, L., Fossi, C., Quattrini, S., et al. (2018). Calcium intake in bone health: A focus on mineral waters. <em>Nutrients</em>, 10(12), 1390.
              <a href="https://doi.org/10.3390/nu10121390" target="_blank" rel="noopener">https://doi.org/10.3390/nu10121390</a>
            </li>
          </ul>

          <h4 style={{margin: '0 0 3mm 0'}}>6. Regulación SNA, cortisol y sistema neuroendocrino</h4>
          <ul style={{margin: '0', paddingLeft: '6mm', listStyle: 'disc'}}>
            <li>
              Zaccaro, A., et al. (2018). How breath-control can change your life: A systematic review on psycho-physiological correlates of slow breathing.
              <em> Frontiers in Human Neuroscience</em>, 12, 353.
            </li>
            <li>
              Noble, D. J., & Hochman, S. (2019). Hypothesis: Pulmonary afferent activity patterns during slow, deep breathing contribute to the neural induction of physiological relaxation.
              <em> Frontiers in Physiology</em>, 10, 1176.
            </li>
            <li>
              Busch, V., et al. (2012). The effect of deep and slow breathing on pain perception, autonomic activity, and mood processing.
              <em> Pain Medicine</em>, 13(2), 215–228.
            </li>
            <li>
              Almahqamh, O., & Hammond, L. (2024). Does the Wim Hof Method have a beneficial impact on physiological and psychological outcomes? A systematic review. <em>PLOS ONE</em>, 19(3), e0286393.
            </li>
            <li>
              Kujawski, S., et al. (2022). Combination of whole-body cryotherapy with static stretching exercises reduces fatigue and improves functioning of the autonomic nervous system in Chronic Fatigue Syndrome.
              <em> Journal of Translational Medicine</em>, 20, 273.
            </li>
            <li>
              Louis, J., et al. (2015). Head exposure to cold during whole-body cryostimulation: Influence on thermal response and autonomic modulation. <em>PLOS ONE</em>, 10(4), e0124776.
            </li>
            <li>
              Schaal, K., et al. (2013). Parasympathetic activity and blood catecholamine responses following a single partial-body cryostimulation and a whole-body cryostimulation. <em>PLOS ONE</em>, 8(8), e72658.
            </li>
            <li>
              Laukkanen, J. A., et al. (2019). Recovery from sauna bathing favorably modulates cardiac autonomic nervous system.
              <em> European Journal of Preventive Cardiology</em>, 26(15), 1636–1642.
            </li>
            <li>
              Hussain, J., & Cohen, M. (2018). Clinical effects of regular dry sauna bathing: A systematic review. <em>Evidence-Based Complementary and Alternative Medicine</em>, 2018, 1857413.
            </li>
            <li>
              Optimización de vagal stimulation protocol based on spontaneous breathing rate. <em>Frontiers in Physiology</em> (2018).
            </li>
            <li>
              Acute effects of the 4–4–8 breathing technique on arterial stiffness in healthy young men. <em>Scientific Reports</em> (2024).
            </li>
            <li>
              Ramos-Campo, D. J., et al. (2024). Cryostimulation and/or cold-water immersion exposure enhance parasympathetic nervous activity: A systematic review and meta-analysis.
              <em> Physiology & Behavior</em>, 274, 114528.
            </li>
            <li>
              Costa-Silva, et al. (2019). Acute effects of cold water immersion on cardiovascular and autonomic responses.
              <em> RIAA</em>, 3(5), 8–13.
            </li>
            <li>
              The effects of cold water immersion with different dosages on heart rate variability indices. <em>Science & Sports</em> (2016).
            </li>
            <li>
              Brennan, V., et al. (2024). Whole-body cold stimulation improves cardiac autonomic control independent of the employed temperature.
              <em> Journal of Clinical Medicine</em>, 13(24), 7728.
            </li>
            <li>
              An alternating breathing pattern significantly affects the brain functional connectivity and mood states. <em>Frontiers in Human Neuroscience</em> (2025).
            </li>
            <li>
              Vagus activation by Cold Face Test reduces acute psychosocial stress responses. <em>PLOS ONE</em> (2022).
            </li>
            <li>
              The use of whole-body cryotherapy: time- and dose-response investigation on circulating blood catecholamines and heart rate variability.
              <em> European Journal of Applied Physiology</em> (2020).
            </li>
            <li>
              Effects of cryotherapy methods on circulatory, metabolic, inflammatory and neural properties: A systematic review. <em>Fisioterapia em Movimento</em> (2016).
            </li>
            <li>
              The Effects of Heat and Massage Application on Autonomic Nervous System. <em>Annals of Rehabilitation Medicine</em> (2011).
            </li>
          </ul>
        </main>
      </Page>
    </div>
  );
};

export default InformeView;
